(ns ui.components.overflow-chips.view
  "A row of chips on one line. The chips that do not fit are hidden, and a count
   such as \"+2\" comes after the last chip that fits. The user sees that the row
   has more values. The title of the row is the text of all the values, so the
   browser shows all of them when the pointer stays on the row. A grid cell has
   a fixed height, so chips that wrap to a second line are cut, and the user does
   not see that more values exist."
  (:require
   [reagent.core :as r]
   [ui.dom-globals :as dg]))

(def badge-width
  "The space in pixels that the count needs after the last chip, with the gap."
  40)

(def gap
  "The space in pixels between two chips."
  4)

(defn fit
  "How many chips fit in the row, and where the count starts. `container-width`
   is the width of the row. `chip-rights` are the right edges of the chips, from
   the left edge of the row, in their order. When all the chips fit, there is no
   count. Else the chips that fit with the count after them are shown. The result
   is `{:visible n :badge-left x}`, and `:badge-left` is nil when there is no count."
  [{:keys [container-width chip-rights]}]
  (let [chip-count (count chip-rights)]
    (if (or (zero? chip-count)
            (<= (last chip-rights) container-width))
      {:visible    chip-count
       :badge-left nil}
      (let [visible (count (take-while (fn [right]
                                         (<= (+ right gap badge-width) container-width))
                                       chip-rights))]
        {:visible    visible
         :badge-left (if (pos? visible)
                       (+ (nth chip-rights (dec visible)) gap)
                       0)}))))

(defn- measure!
  [{:keys [container* chip-nodes* state*]}]
  (when-let [container @container*]
    (let [nodes (->> @chip-nodes*
                     (sort-by key)
                     (map val)
                     (remove nil?))
          next-state (fit {:container-width (dg/client-width container)
                           :chip-rights     (mapv (fn [node]
                                                    (+ (dg/offset-left node)
                                                       (dg/offset-width node)))
                                                  nodes)})]
      (when (not= next-state @state*)
        (reset! state* next-state)))))

(defn view
  "Render the chips. `:chips` is a vector of the hiccup of the chips. `:title` is
   the text of all the values. `:badge-class` is the class of the count. `:class`
   is added to the classes of the row."
  [_props]
  (let [container* (atom nil)
        chip-nodes* (atom {})
        state* (r/atom {:visible    ##Inf
                        :badge-left nil})
        stop-observing* (atom nil)
        refs {:container*  container*
              :chip-nodes* chip-nodes*
              :state*      state*}]
    (r/create-class
     {:display-name "overflow-chips"
      :component-did-mount
      (fn [_]
        (reset! stop-observing* (dg/observe-resize! [@container*]
                                                    (fn []
                                                      (measure! refs))))
        (measure! refs))
      :component-did-update
      (fn [_ _]
        (measure! refs))
      :component-will-unmount
      (fn [_]
        (when-let [stop! @stop-observing*]
          (stop!)))
      :reagent-render
      (fn [{:keys [chips title badge-class class]}]
        (let [{:keys [visible badge-left]} @state*
              chip-count (count chips)]
          (into
           [:div {:class (str "relative flex h-full w-full flex-nowrap items-center gap-1 overflow-hidden "
                              class)
                  :title title
                  :ref   (fn [node]
                           (reset! container* node))}]
           (concat
            (map-indexed
             (fn [idx chip]
               ^{:key idx}
               [:span {:class "inline-flex shrink-0"
                       :style (when (>= idx visible)
                                {:visibility "hidden"})
                       :ref   (fn [node]
                                (if node
                                  (swap! chip-nodes* assoc idx node)
                                  (swap! chip-nodes* dissoc idx)))}
                chip])
             chips)
            (when (and (some? badge-left)
                       (< visible chip-count))
              [^{:key "overflow-count"}
               [:span {:class badge-class
                       :style {:position  "absolute"
                               :top       "50%"
                               :transform "translateY(-50%)"
                               :left      badge-left}}
                (str "+" (- chip-count visible))]])))))})))
